import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type {
  AppealVersionResponse,
  CampaignDetail,
  ConfigVersionView,
  CreateConfigVersionResponse,
  CreateCreativeVersionResponse,
  CreativeFormatKey,
  CreativeVersionView,
  VersionAppeal,
  WithdrawCandidateResponse,
} from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { Banner, Button, Card, Dialog, Input, StatusPill, Textarea, type Tone } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { amountValidationMessage, compareDecimal, validateAmountInput } from "../../lib/money";
import { useIntentKey } from "../../lib/useIntentKey";
import {
  blockingIssues,
  categoryLabel,
  CategoryPicker,
  CreativeValidationPanel,
  fetchValidation,
  FormatPicker,
  isFormatKey,
  issueMessage,
  unavailableCategories,
  useTargetingCategories,
} from "./campaignInputs";
import { trimZeros } from "./CampaignSettings";

// The candidate lifecycle on the campaign page (UX-DESIGN.md §2.3 "only one
// candidate", §2.4 Revise / Appeal; web/API-GAPS.md G-19, migration 0061):
//
//   Withdraw  POST /v1/campaigns/{id}/candidates/withdraw {config_version_id, creative_version_id}
//             — the pending_candidate exactly as shown (candidates.go:43-86).
//   Revise    POST …/config-versions or …/creative-versions: the new version
//             supersedes the pending one of its half and the response lists
//             superseded_version_ids (0061:566, 599).
//   Appeal    POST /v1/campaigns/{id}/versions/{version_id}/appeal {basis}
//             — once per rejected, current, not-withdrawn version (0061:793-871).
//
// Each carries one idempotency key per intent (G-21); refusals are typed.

const APPEAL_MAX = 2000;

function isEnded(c: CampaignDetail): boolean {
  return c.states.includes("ended") || !!c.ended_at;
}

function useRefreshCampaign(campaignId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
    void qc.invalidateQueries({ queryKey: ["campaigns"] });
  };
}

const APPEAL_STATE: Record<VersionAppeal["state"], { tone: Tone; label: string }> = {
  pending: { tone: "info", label: "Appeal pending" },
  granted: { tone: "success", label: "Appeal granted" },
  denied: { tone: "error", label: "Appeal denied" },
  withdrawn: { tone: "neutral", label: "Appeal withdrawn" },
};

/** The 0061 per-version rows: targeting, regulated categories, withdrawal, appeal (and the Appeal action). */
export function VersionLifecycleRows({
  c,
  kind,
  v,
}: {
  c: CampaignDetail;
  kind: "config" | "creative";
  v: ConfigVersionView | CreativeVersionView;
}) {
  const categories = useTargetingCategories();
  const cfg = kind === "config" ? (v as ConfigVersionView) : null;
  const id = cfg ? cfg.config_version_id : (v as CreativeVersionView).creative_version_id;
  const targeted = cfg?.targeting.categories ?? [];
  const current = c.pending_candidate?.config_version_id === id || c.pending_candidate?.creative_version_id === id;
  const canAppeal = v.status === "rejected" && !v.withdrawn && !v.appeal && current && !isEnded(c);
  return (
    <>
      {cfg ? (
        <>
          <dt>Categories</dt>
          <dd>{targeted.length ? targeted.map((k) => categoryLabel(categories.data?.categories, k)).join(", ") : "Every context (untargeted)"}</dd>
          {cfg.regulated_categories.length ? (
            <>
              <dt>Regulated</dt>
              <dd>
                <StatusPill tone="warning">
                  {cfg.regulated_categories.map((k) => categoryLabel(categories.data?.categories, k)).join(", ")}
                </StatusPill>{" "}
                <span className="xs muted">A reviewer confirms each regulated category explicitly.</span>
              </dd>
            </>
          ) : null}
          {cfg.publisher_exclusions.length ? (
            <>
              <dt>Excluded publishers</dt>
              <dd>{cfg.publisher_exclusions.length}</dd>
            </>
          ) : null}
        </>
      ) : null}
      {v.withdrawn ? (
        <>
          <dt>{v.withdrawn.reason === "superseded" ? "Replaced" : "Withdrawn"}</dt>
          <dd>
            <StatusPill tone="neutral">{v.withdrawn.reason === "superseded" ? "Replaced by a newer version" : "Withdrawn"}</StatusPill>{" "}
            <span className="xs muted">{formatLocal(v.withdrawn.withdrawn_at)}</span>
          </dd>
        </>
      ) : null}
      {v.appeal ? (
        <>
          <dt>Appeal</dt>
          <dd>
            <span className="stack-sm">
              <span>
                <StatusPill tone={APPEAL_STATE[v.appeal.state]?.tone ?? "neutral"}>{APPEAL_STATE[v.appeal.state]?.label ?? v.appeal.state}</StatusPill>{" "}
                <span className="xs muted">filed {formatLocal(v.appeal.filed_at)}</span>
              </span>
              <span className="small">“{v.appeal.basis}”</span>
              {v.appeal.decided_at ? (
                <span className="small">
                  Decided {formatLocal(v.appeal.decided_at)}
                  {v.appeal.decision_reason ? `: ${v.appeal.decision_reason}` : ""}
                </span>
              ) : (
                <span className="xs muted">A different reviewer from the one who rejected it decides.</span>
              )}
            </span>
          </dd>
        </>
      ) : null}
      {canAppeal ? (
        <>
          <dt>
            <span className="visually-hidden">Appeal</span>
          </dt>
          <dd>
            <AppealButton c={c} versionId={id} kind={kind} />
          </dd>
        </>
      ) : null}
    </>
  );
}

function AppealButton({ c, versionId, kind }: { c: CampaignDetail; versionId: string; kind: "config" | "creative" }) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [open, setOpen] = useState(false);
  const [basis, setBasis] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<AppealVersionResponse | null>(null);
  const m = useMutation({
    mutationFn: (b: string) =>
      ep.advertiser.appealVersion(c.campaign_id, versionId, b, { idempotencyKey: intent.keyFor(`${versionId}:${b}`) }),
    onSuccess: (res) => {
      intent.reset();
      setFiled(res);
      setOpen(false);
      refresh();
    },
    onError: (err) => {
      // Already decided server-side: the page is stale, so refresh it.
      if (err instanceof ApiError && ["appeal_already_filed", "version_not_current", "version_withdrawn", "version_not_rejected"].includes(err.code ?? ""))
        refresh();
    },
  });
  const what = kind === "config" ? "targeting & bid version" : "creative version";

  function submit(e: FormEvent) {
    e.preventDefault();
    const b = basis.trim();
    const n = [...b].length;
    if (n === 0 || n > APPEAL_MAX) {
      setError(`Explain why the decision should be reconsidered, in 1–${APPEAL_MAX.toLocaleString("en-US")} characters.`);
      return;
    }
    setError(null);
    m.mutate(b);
  }

  if (filed) {
    return (
      <Banner tone="success" live="polite" title={filed.replayed ? "Appeal already filed" : "Appeal filed"}>
        Filed {formatLocal(filed.filed_at)}. A different reviewer from the one who rejected it will decide. What's live (if anything)
        keeps serving meanwhile.
      </Banner>
    );
  }
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Appeal this rejection <span className="visually-hidden">({what})</span>
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        dismissible={!m.isPending}
        title={`Appeal the rejected ${what}`}
        description="Each version can be appealed once. A different reviewer re-reviews it; if granted, it goes through approval again."
      >
        <form className="stack" onSubmit={submit} noValidate>
          <Textarea
            label="Why should this be reconsidered?"
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            error={error}
            hint={`${[...basis.trim()].length.toLocaleString("en-US")}/${APPEAL_MAX.toLocaleString("en-US")} characters.`}
          />
          {m.isError ? <CommandError error={m.error} /> : null}
          <div className="row">
            <Button type="submit" loading={m.isPending}>
              File appeal
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={m.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

/** Withdraw the pending candidate: the exact pair on screen, or nothing. */
export function WithdrawControl({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<WithdrawCandidateResponse | null>(null);
  const pair = c.pending_candidate;
  const m = useMutation({
    mutationFn: () => {
      const body = { config_version_id: pair?.config_version_id ?? null, creative_version_id: pair?.creative_version_id ?? null };
      return ep.advertiser.withdrawCandidate(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) });
    },
    onSuccess: (res) => {
      intent.reset();
      setConfirming(false);
      setDone(res);
      refresh();
    },
    onError: (err) => {
      setConfirming(false);
      // The pending pair changed (or vanished) under this page: show the current one.
      if (err instanceof ApiError && (err.code === "candidate_mismatch" || err.code === "no_pending_candidate")) refresh();
    },
  });

  if (done) {
    return (
      <Banner tone="success" live="polite" title={done.replayed ? "Already withdrawn" : "Pending versions withdrawn"}>
        {done.withdrawn_version_ids.length === 1 ? "1 version was" : `${done.withdrawn_version_ids.length} versions were`} withdrawn{" "}
        {formatLocal(done.withdrawn_at)}. What's live (if anything) is unchanged. Withdrawn versions can't be reviewed, accepted or
        promoted.
      </Banner>
    );
  }
  if (!pair || isEnded(c)) return null;
  return (
    <div className="stack-sm">
      {m.isError ? <CommandError error={m.error} /> : null}
      <div>
        <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
          Withdraw pending versions
        </Button>
      </div>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        alert
        dismissible={!m.isPending}
        title="Withdraw the pending versions?"
        description="They leave review and can't be reviewed, accepted or promoted again. What's live keeps serving. To change them instead, use Revise."
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={m.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => m.mutate()} loading={m.isPending}>
              Withdraw
            </Button>
          </>
        }
      />
    </div>
  );
}

function SupersededNote({ ids }: { ids: string[] | null }) {
  if (!ids || ids.length === 0) return <span>Nothing pending was replaced.</span>;
  return (
    <span>
      It replaced the pending {ids.length === 1 ? "version" : "versions"}{" "}
      {ids.map((id, i) => (
        <span key={id}>
          {i > 0 ? ", " : ""}
          <code className="xs">{id}</code>
        </span>
      ))}
      , which can no longer be reviewed.
    </span>
  );
}

/** Revise = a new version of one half, which supersedes that half's pending version. */
export function ReviseCard({ c }: { c: CampaignDetail }) {
  const [mode, setMode] = useState<"creative" | "config" | null>(null);
  const [result, setResult] = useState<{ kind: "creative" | "config"; res: CreateCreativeVersionResponse | CreateConfigVersionResponse } | null>(null);
  if (isEnded(c)) return null;
  const creativeBase = c.pending?.creative_version ?? c.live?.creative_version ?? null;
  const configBase = c.pending?.config_version ?? c.live?.config_version ?? null;
  return (
    <Card
      title="Revise"
      subtitle="Submit a new version of the creative, or of the targeting and bid. It replaces whatever is pending for that half; what's live keeps serving until the new version is approved and promoted."
    >
      <div className="stack">
        {result ? (
          <Banner tone="success" live="polite" title={result.kind === "creative" ? "New creative version saved" : "New targeting & bid version saved"}>
            <div className="stack-sm">
              <span>
                Version {result.res.version_no} is a draft. <SupersededNote ids={result.res.superseded_version_ids} />
              </span>
              <span className="small">Accept the commercial terms for the new pending versions below to submit them for review.</span>
            </div>
          </Banner>
        ) : null}
        {mode === null ? (
          <div className="row">
            <Button variant="secondary" onClick={() => (setResult(null), setMode("creative"))}>
              Revise creative
            </Button>
            {configBase ? (
              <Button variant="secondary" onClick={() => (setResult(null), setMode("config"))}>
                Revise targeting &amp; bid
              </Button>
            ) : null}
          </div>
        ) : mode === "creative" ? (
          <ReviseCreativeForm
            c={c}
            base={creativeBase}
            onCancel={() => setMode(null)}
            onDone={(res) => {
              setResult({ kind: "creative", res });
              setMode(null);
            }}
          />
        ) : configBase ? (
          <ReviseConfigForm
            c={c}
            base={configBase}
            onCancel={() => setMode(null)}
            onDone={(res) => {
              setResult({ kind: "config", res });
              setMode(null);
            }}
          />
        ) : null}
      </div>
    </Card>
  );
}

function ReviseCreativeForm({
  c,
  base,
  onCancel,
  onDone,
}: {
  c: CampaignDetail;
  base: CreativeVersionView | null;
  onCancel: () => void;
  onDone: (res: CreateCreativeVersionResponse) => void;
}) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [title, setTitle] = useState(base?.title ?? "");
  const [body, setBody] = useState(base?.body ?? "");
  const [format, setFormat] = useState<CreativeFormatKey | "">(base && isFormatKey(base.format) ? base.format : "");
  const [destination, setDestination] = useState(base?.destination_url ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const req = { title: title.trim(), body: body.trim(), format, destination_url: destination.trim() };
  const m = useMutation({
    mutationFn: async () => {
      if (!format) throw new Error("Choose a format.");
      const v = await fetchValidation(qc, ep, req);
      const blocking = blockingIssues(v);
      if (blocking.length) {
        const fe: Record<string, string> = {};
        for (const i of blocking) {
          const f = i.field === "title" || i.field === "body" ? i.field : i.field === "destination_url" ? "destination" : "creative";
          fe[f] = fe[f] ? `${fe[f]} ${issueMessage(i, v.destination.host)}` : issueMessage(i, v.destination.host);
        }
        setErrors(fe);
        return null;
      }
      const payload = { ...req, format };
      return ep.advertiser.createCreativeVersion(c.campaign_id, payload, { idempotencyKey: intent.keyFor(JSON.stringify(payload)) });
    },
    onSuccess: (res) => {
      if (!res) return;
      intent.reset();
      refresh();
      onDone(res);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!req.title) fe.title = "Enter a title.";
    if (!req.body) fe.body = "Enter the body text.";
    if (!req.destination_url) fe.destination = "Enter the destination URL.";
    if (!format) fe.format = "Choose a format.";
    setErrors(fe);
    if (Object.keys(fe).length === 0) m.mutate();
  }

  return (
    <form className="stack" onSubmit={submit} noValidate aria-label="Revise creative">
      <FormatPicker value={format} onChange={setFormat} />
      {errors.format ? <div className="field-error" role="alert">{errors.format}</div> : null}
      <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={errors.title} />
      <Textarea label="Body" value={body} onChange={(e) => setBody(e.target.value)} error={errors.body} />
      <Input label="Destination URL" type="url" value={destination} onChange={(e) => setDestination(e.target.value)} error={errors.destination} />
      <CreativeValidationPanel req={req} />
      {errors.creative ? (
        <div className="field-error" role="alert">
          {errors.creative}
        </div>
      ) : null}
      {m.isError ? <CommandError error={m.error} /> : null}
      <div className="row">
        <Button type="submit" loading={m.isPending}>
          Save new creative version
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={m.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ReviseConfigForm({
  c,
  base,
  onCancel,
  onDone,
}: {
  c: CampaignDetail;
  base: ConfigVersionView;
  onCancel: () => void;
  onDone: (res: CreateConfigVersionResponse) => void;
}) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const categories = useTargetingCategories();
  const currency = base.currency;
  const [chosen, setChosen] = useState<string[]>(base.targeting.categories ?? []);
  const [bid, setBid] = useState(trimZeros(base.bid_amount));
  const [lifetime, setLifetime] = useState(base.lifetime_budget ? trimZeros(base.lifetime_budget) : "");
  const [daily, setDaily] = useState(base.daily_budget ? trimZeros(base.daily_budget) : "");
  const [freqCapMax, setFreqCapMax] = useState(base.frequency_cap ? String(base.frequency_cap.max_impressions) : "");
  const [freqCapWindow, setFreqCapWindow] = useState(base.frequency_cap ? String(base.frequency_cap.window_hours) : "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: (body: Parameters<typeof ep.advertiser.createConfigVersion>[1]) =>
      ep.advertiser.createConfigVersion(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) }),
    onSuccess: (res) => {
      intent.reset();
      refresh();
      onDone(res);
    },
  });
  const per = base.pricing_model === "cpm" ? "per 1,000 impressions" : "per engagement";

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    const bidV = validateAmountInput(bid, currency);
    const lifeV = validateAmountInput(lifetime, currency);
    const dailyV = validateAmountInput(daily, currency);
    if (!bidV.ok) fe.bid = amountValidationMessage(bidV.reason, currency);
    if (!lifeV.ok) fe.lifetime = lifetime.trim() ? amountValidationMessage(lifeV.reason, currency) : "Enter a lifetime budget.";
    else if (bidV.ok && compareDecimal(lifeV.value, bidV.value) < 0) fe.lifetime = "The lifetime budget must be at least one bid.";
    if (daily.trim()) {
      if (!dailyV.ok) fe.daily = amountValidationMessage(dailyV.reason, currency);
      else if (lifeV.ok && compareDecimal(dailyV.value, lifeV.value) > 0) fe.daily = "The daily budget can't be more than the lifetime budget.";
    }
    const blocked = unavailableCategories(chosen, categories.data?.categories);
    if (blocked.length) fe.categories = `${blocked.map((k) => categoryLabel(categories.data?.categories, k)).join(", ")} can't be targeted right now.`;
    if (!!freqCapMax.trim() !== !!freqCapWindow.trim()) {
      fe.freqCapMax = "Set both the impression cap and the window, or leave both empty.";
    } else if (freqCapMax.trim() && freqCapWindow.trim()) {
      const maxN = Number(freqCapMax);
      const windowN = Number(freqCapWindow);
      if (!Number.isInteger(maxN) || maxN < 1 || maxN > 100) fe.freqCapMax = "Enter a whole number from 1 to 100.";
      if (!Number.isInteger(windowN) || windowN < 1 || windowN > 720) fe.freqCapWindow = "Enter a whole number of hours from 1 to 720.";
    }
    setErrors(fe);
    if (Object.keys(fe).length || !bidV.ok || !lifeV.ok) return;
    m.mutate({
      currency,
      bid_type: base.pricing_model,
      bid_amount: bidV.value,
      targeting: chosen.length ? { categories: [...chosen] } : {},
      publisher_exclusions: base.publisher_exclusions,
      category_exclusions: base.category_exclusions,
      budget_timezone: base.budget_timezone,
      lifetime_budget: lifeV.value,
      daily_budget: daily.trim() && dailyV.ok ? dailyV.value : null,
      frequency_cap:
        freqCapMax.trim() && freqCapWindow.trim() ? { max_impressions: Number(freqCapMax), window_hours: Number(freqCapWindow) } : null,
    });
  }

  return (
    <form className="stack" onSubmit={submit} noValidate aria-label="Revise targeting and bid">
      <p className="small muted">
        Pricing model {base.pricing_model.toUpperCase()} and currency {currency} stay as they are: a campaign has one pricing model.
        {base.publisher_exclusions.length ? ` Your ${base.publisher_exclusions.length} publisher exclusions are kept.` : ""}
      </p>
      <CategoryPicker value={chosen} onChange={setChosen} error={errors.categories} />
      <Input label={`Bid ${per}`} inputMode="decimal" value={bid} onChange={(e) => setBid(e.target.value)} error={errors.bid} />
      <div className="grid-2">
        <Input label="Lifetime budget" inputMode="decimal" value={lifetime} onChange={(e) => setLifetime(e.target.value)} error={errors.lifetime} />
        <Input label="Daily budget" optional inputMode="decimal" value={daily} onChange={(e) => setDaily(e.target.value)} error={errors.daily} />
      </div>
      <div className="grid-2">
        <Input
          label="Frequency cap: impressions"
          optional
          inputMode="numeric"
          value={freqCapMax}
          onChange={(e) => setFreqCapMax(e.target.value)}
          error={errors.freqCapMax}
          hint="1-100 per session, within the window."
        />
        <Input
          label="Frequency cap: window (hours)"
          optional
          inputMode="numeric"
          value={freqCapWindow}
          onChange={(e) => setFreqCapWindow(e.target.value)}
          error={errors.freqCapWindow}
          hint="1-720. Leave both empty for no cap."
        />
      </div>
      {m.isError ? <CommandError error={m.error} /> : null}
      <div className="row">
        <Button type="submit" loading={m.isPending}>
          Save new targeting &amp; bid version
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={m.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
